<template>
  <b-modal
    v-model="isModalVisible"
    :title="$t('ส่งคำขอสรุปยอดอีกครั้ง')"
    centered
    :ok-disabled="isSubmitting"
    @ok="handleSubmit"
    @hidden="resetForm"
  >
    <b-form>
      <b-form-group
        :label="$t('วันที่เริ่มต้น')"
        label-for="start-date"
      >
        <flat-pickr
          id="start-date"
          v-model="formData.startDate"
          class="form-control"
          :config="dateConfig"
          :disabled="isSubmitting"
        />
      </b-form-group>

      <b-form-group
        :label="$t('วันที่สิ้นสุด')"
        label-for="end-date"
      >
        <flat-pickr
          id="end-date"
          v-model="formData.endDate"
          class="form-control"
          :config="dateConfig"
          :disabled="isSubmitting"
        />
      </b-form-group>

      <b-alert
        variant="warning"
        show
      >
        <div class="alert-body">
          <feather-icon
            icon="AlertTriangleIcon"
            class="mr-50"
          />
          <span>{{ $t('การสรุปยอดจะอัพเดทข้อมูลทั้งหมดในช่วงวันที่ที่เลือก และส่งผลกระทบต่อ Invoice โดยอัตโนมัติ') }}</span>
        </div>
      </b-alert>
    </b-form>

    <template #modal-footer="{ ok, cancel }">
      <b-button
        variant="outline-secondary"
        :disabled="isSubmitting"
        @click="cancel()"
      >
        {{ $t('ยกเลิก') }}
      </b-button>
      <b-button
        variant="primary"
        :disabled="isSubmitting"
        @click="ok()"
      >
        <b-spinner
          v-if="isSubmitting"
          small
          class="mr-50"
        />
        {{ isSubmitting ? $t('กำลังดำเนินการ...') : $t('ส่งคำขอ') }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal,
  BForm,
  BFormGroup,
  BButton,
  BAlert,
  BSpinner,
} from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import lottoService from '@/service/lottoService'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BModal,
    BForm,
    BFormGroup,
    BButton,
    BAlert,
    BSpinner,
    flatPickr,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    selectedMonth: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isSubmitting: false,
      formData: {
        startDate: '',
        endDate: '',
        triggerInvoice: true,
      },
      dateConfig: {
        dateFormat: 'd/m/Y',
        allowInput: true,
      },
    }
  },
  computed: {
    isModalVisible: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.initializeDates()
      }
    },
  },
  methods: {
    pad2(number) {
      return (number < 10 ? '0' : '') + number
    },
    initializeDates() {
      // Parse selectedMonth (format: "MM/YYYY")
      const [month, year] = this.selectedMonth.split('/')
      const monthNum = parseInt(month, 10) - 1 // 0-indexed
      const yearNum = parseInt(year, 10)

      // First day of month
      const startDate = new Date(yearNum, monthNum, 1)

      // Last day of month
      const endDate = new Date(yearNum, monthNum + 1, 0)

      // Format as dd/MM/yyyy
      this.formData.startDate = `${this.pad2(startDate.getDate())}/${this.pad2(startDate.getMonth() + 1)}/${startDate.getFullYear()}`
      this.formData.endDate = `${this.pad2(endDate.getDate())}/${this.pad2(endDate.getMonth() + 1)}/${endDate.getFullYear()}`
    },
    validateDates() {
      // Parse dates
      const [startDay, startMonth, startYear] = this.formData.startDate.split('/')
      const [endDay, endMonth, endYear] = this.formData.endDate.split('/')

      const start = new Date(parseInt(startYear, 10), parseInt(startMonth, 10) - 1, parseInt(startDay, 10))
      const end = new Date(parseInt(endYear, 10), parseInt(endMonth, 10) - 1, parseInt(endDay, 10))

      if (start > end) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('ข้อมูลไม่ถูกต้อง'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: this.$t('วันที่เริ่มต้นต้องไม่มากกว่าวันที่สิ้นสุด'),
          },
        })
        return false
      }

      return true
    },
    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()

      if (!this.validateDates()) {
        return
      }

      const confirmed = await this.$bvModal.msgBoxConfirm(
        this.$t('คุณแน่ใจหรือไม่ ว่าจะส่งคำขอเพื่อสรุปยอดใหม่ ตั้งแต่วันที่ {startDate} ถึง {endDate} การดำเนินการจะไม่สามารถย้อนกลับได้', {
          startDate: this.formData.startDate,
          endDate: this.formData.endDate,
        }),
        {
          title: this.$t('ยืนยันการสรุปยอดอีกครั้ง'),
          size: 'md',
          okVariant: 'danger',
          okTitle: this.$t('ยืนยัน'),
          cancelTitle: this.$t('ยกเลิก'),
          hideHeaderClose: false,
          centered: true,
        },
      )

      if (!confirmed) {
        return
      }

      this.isSubmitting = true

      try {
        const response = await lottoService.postAuthen('summary/rebuild', {
          startDate: this.formData.startDate,
          endDate: this.formData.endDate,
          triggerInvoice: this.formData.triggerInvoice,
        })

        if (response.status === 200) {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('สำเร็จ'),
              icon: 'CheckCircleIcon',
              variant: 'success',
              text: this.$t('ระบบได้ส่งคำขอการสรุปยอดสำหรับวัน {startDate} - {endDate} แล้ว', {
                startDate: this.formData.startDate,
                endDate: this.formData.endDate,
              }),
            },
          })

          this.$emit('success', {
            startDate: this.formData.startDate,
            endDate: this.formData.endDate,
          })

          this.isModalVisible = false
        } else {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('เกิดข้อผิดพลาด'),
              icon: 'AlertCircleIcon',
              variant: 'danger',
              text: response.data?.error || response.data?.message || this.$t('ไม่สามารถส่ง request ได้ ({status} {statusText})', {
                status: response.status,
                statusText: response.statusText,
              }),
            },
          })
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('เกิดข้อผิดพลาด'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: error.response?.data?.error || error.message || this.$t('ไม่สามารถส่ง request ได้'),
          },
        })
      } finally {
        this.isSubmitting = false
      }
    },
    resetForm() {
      this.formData = {
        startDate: '',
        endDate: '',
        triggerInvoice: true,
      }
      this.isSubmitting = false
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
